import styled from "@emotion/styled";
import type { TextFieldProps } from "@mui/material";
import { Popover, TextField } from "@mui/material";
import { colord, extend, getFormat } from "colord";
import namesPlugin from "colord/plugins/names";
import { useCallback, useMemo, useState, type ChangeEvent, type KeyboardEvent, type MouseEvent } from "react";

import { useDebouncedFn } from "../utilities";
import { zIndexes } from "../utilities/zIndexes";
import { ColorFormatSelect } from "./ColorFormatSelect";
import { convertColor, type Format } from "./colorHelpers";
import { ColorPicker } from "./ColorPicker";

extend([namesPlugin]);

const ColorPickerContainer = styled.div`
  display: block;
  width: 100%;
`;

const ColorPickerButton = styled.button`
  height: 26px;
  width: 26px;
  padding: 0;
  margin: 0 10px 0 0;
  min-width: 0;
  outline: none;
  border: solid 1px ${({ theme }) => theme.outlineSoft};
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;

  > span {
    display: block;
    height: 100%;
    width: 100%;
  }

  &:hover {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const ColorTextField = styled(TextField)`
  width: 100%;
  height: 100%;

  input {
    text-align: left;
    height: 24px;
  }
`;

const ColorPickerInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 30px;

  ${ColorPickerButton} {
    flex: none;
  }
`;

const ColorPickerPopover = styled(Popover)`
  z-index: ${zIndexes.colorPickerPopover};

  .MuiPaper-root {
    overflow: visible;
    border-radius: 8px;
  }
`;

const ColorPickerPopoverContent = styled.div`
  padding: 5px;
`;

type EditingState = {
  value: string | null;
  basedOnValue: string;
};

export type ColorPickerInputProps = Omit<TextFieldProps, "value" | "onChange"> & {
  value: string;
  onChange: (color: string) => void;
  setError?: (error: string | undefined) => void;
};

export const ColorPickerInput = ({ value, onChange, setError, onFocus, ...props }: ColorPickerInputProps) => {
  const dataTest = (props as Record<string, string>)["data-test"];

  // Create a stable debounced function that never changes
  const debouncedOnChange = useDebouncedFn(onChange, 300);

  // Track editing state and the value it was based on
  const [editingState, setEditingState] = useState<EditingState>({ value: null, basedOnValue: value });

  // Reset editing state if the external value has changed
  const editingValue = editingState.basedOnValue === value ? editingState.value : null;

  // UI state
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const popoverOpen = Boolean(anchorEl && !props.disabled);
  const id = popoverOpen ? "simple-popover" : undefined;

  const colorFormat = useMemo<Format>(() => getFormat(value) ?? "rgb", [value]);

  // Derive current values
  const currentValue = editingValue ?? value;
  const isEditing = editingValue !== null;

  const commitValue = useCallback(
    (updatedValue: { color?: string; format?: Format }) => {
      let color = updatedValue.color ?? currentValue;
      const format = updatedValue.format ?? getFormat(color);

      if (format === undefined) {
        // check if it is a hex value missing the #
        if (getFormat(`#${color}`) === "hex") {
          color = `#${color}`;
        }
      }

      const convertedColor = convertColor(color, format, setError);

      // allow falling back to base value if conversion fails
      color = convertedColor ?? value;

      if (color === "" || colord(color).isValid()) {
        setEditingState((prev) => ({ ...prev, value: color }));
        debouncedOnChange(color);
      }
    },
    [currentValue, debouncedOnChange, setError, value],
  );

  const handleColorFormatChange = useCallback((format: Format) => commitValue({ format }), [commitValue]);

  const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) =>
    setEditingState({ value: target.value, basedOnValue: value });

  const handleInputBlur = () => {
    // Commit on blur
    if (isEditing) {
      commitValue({ color: currentValue });
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text when input is focused
    e.target.select();
    onFocus?.(e);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "Enter":
        // Blur on enter.
        (e.target as HTMLInputElement).blur();
        break;
      case "Escape":
        // Reset input without committing
        setEditingState({ value: null, basedOnValue: value });
        (e.target as HTMLInputElement).blur();
        break;
    }
  };

  const handlePickerChange = useCallback(
    (newColor: string) => {
      const capitalizedColor = newColor.toUpperCase();
      setEditingState({ value: capitalizedColor, basedOnValue: value });
      debouncedOnChange(capitalizedColor);
    },
    [debouncedOnChange, value],
  );

  const inputValid = useMemo(() => currentValue === "" || colord(currentValue).isValid(), [currentValue]);

  const previewColor = useMemo(() => {
    return colord(currentValue).isValid() ? currentValue : value;
  }, [currentValue, value]);

  return (
    <ColorPickerContainer>
      <ColorPickerInputContainer>
        <ColorPickerButton
          type="button" // ensure the button does not submit (https://github.com/mui/material-ui/issues/27739)
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
          }}
        >
          {previewColor && <span style={{ background: previewColor }} />}
        </ColorPickerButton>
        <ColorTextField
          {...props}
          value={currentValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleFocus}
          onKeyDown={handleInputKeyDown}
          error={!inputValid}
          spellCheck={false}
          variant="standard"
          slotProps={{
            input: {
              style: { paddingLeft: 0, paddingRight: 0 },
            },
            htmlInput: { "data-test": `${dataTest ?? "color"}-input` },
          }}
        />
        <ColorFormatSelect value={colorFormat} onChange={handleColorFormatChange} />
      </ColorPickerInputContainer>

      <ColorPickerPopover
        id={id}
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <ColorPickerPopoverContent
          data-test="color-picker-container"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <ColorPicker value={currentValue} onChange={handlePickerChange} />
        </ColorPickerPopoverContent>
      </ColorPickerPopover>
    </ColorPickerContainer>
  );
};
